import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, Square, Plus, Trash2, Loader2, Calendar, User, Bot, X, AlertTriangle, StickyNote, Users, AlertCircle, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { tasksApi, chatApi, customersApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import TaskDetailModal, { taskAssigneeNames } from "../components/TaskDetailModal";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; }
};

// Parse a date-only ("YYYY-MM-DD") or ISO string as a LOCAL calendar day.
const asLocalDay = (s) => {
  if (!s) return null;
  const d = s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Smart due-date bucket: overdue / today / tomorrow / week / later / none.
const BUCKETS = [
  { key: "overdue", label: "Overdue", tone: "text-rose-600" },
  { key: "today", label: "Today", tone: "text-blue-600" },
  { key: "tomorrow", label: "Tomorrow", tone: "text-slate-700" },
  { key: "week", label: "This Week", tone: "text-slate-700" },
  { key: "later", label: "Later", tone: "text-slate-700" },
  { key: "none", label: "No due date", tone: "text-slate-400" },
];

const dueBucket = (dueAt, status) => {
  if (!dueAt) return "none";
  const day = asLocalDay(dueAt);
  if (!day) return "none";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((day - today) / 86400000);
  // Completed past-due tasks aren't "overdue" — bucket them by date only.
  if (diff < 0) return status === "done" ? "later" : "overdue";
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff <= 7) return "week";
  return "later";
};

export default function Tasks() {
  const { user } = useAuth();
  const canManage = user && (user.role === "admin" || user.role === "dispatcher");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // open | done | all
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [openCustomerId, setOpenCustomerId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const data = await tasksApi.list(params);
      setTasks(data);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => { await load(); })();
  }, [filter]);

  useEffect(() => {
    // chat/members is readable by every role (unlike /users which is admin-only),
    // so dispatchers/technicians creating a task also get the full assignee list.
    chatApi.members().then((u) => setUsers(u.map((x) => ({ id: x.id, name: x.name || x.email, email: x.email })))).catch(() => {});
    customersApi.list().then((c) => setCustomers(c.map((x) => ({ id: x.id, name: x.name })))).catch(() => {});
  }, []);

  const notifyChanged = () => window.dispatchEvent(new Event("tasks-changed"));

  const toggle = async (task) => {
    const next = task.status === "done" ? "open" : "done";
    if (next === "done" && !window.confirm("Mark this task as complete?")) return;
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: next } : t)));
    try {
      await tasksApi.update(task.id, { status: next });
      notifyChanged();
      if (filter !== "all") setTimeout(load, 250);
    } catch {
      toast.error("Could not update task");
      load();
    }
  };

  const remove = async (task) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await tasksApi.remove(task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      notifyChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const openCount = tasks.filter((t) => t.status === "open").length;

  // Group the loaded tasks by smart due-date bucket (ordered), sorted by due date within.
  const grouped = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      const b = dueBucket(t.dueAt, t.status);
      (map[b] = map[b] || []).push(t);
    });
    const sortFn = (a, b) => {
      const da = asLocalDay(a.dueAt)?.getTime() ?? Infinity;
      const db = asLocalDay(b.dueAt)?.getTime() ?? Infinity;
      if (da !== db) return da - db;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    };
    return BUCKETS.map((b) => ({ ...b, items: (map[b.key] || []).sort(sortFn) })).filter((g) => g.items.length);
  }, [tasks]);

  usePageHeader({
    title: "Tasks",
    subtitle: "Follow-ups created manually or by your automations",
    actions:
      canManage && (
        <Button onClick={() => setAddOpen(true)} data-testid="new-task-btn" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
          <Plus className="h-4 w-4 mr-1.5" /> New task
        </Button>
      ),
  });

  return (
    <>
      <div className="p-6 max-w-3xl">
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 mb-4" data-testid="task-filters">
          {[["open", `Open${openCount ? ` (${openCount})` : ""}`], ["done", "Done"], ["all", "All"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`task-filter-${k}`}
              className={`h-8 px-4 rounded-md text-[13px] font-semibold transition-colors ${filter === k ? "md:bg-white md:text-slate-900 shadow-sm max-[767px]:bg-[#FFFFFF] max-[767px]:!text-[#353434]" : "text-slate-500 hover:text-slate-800"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : tasks.length === 0 ? (
          <div data-testid="tasks-empty" className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center">
            <CheckSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-slate-600">No {filter === "all" ? "" : filter} tasks</div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="tasks-list">
            {grouped.map((group) => (
              <div key={group.key} data-testid={`task-group-${group.key}`}>
                <div className="flex items-center gap-2 px-1 mb-2 max-[767px]:!text-[#353434]">
                  {group.key === "overdue" && <AlertCircle className="h-3.5 w-3.5 text-rose-500" />}
                  <h3 className={`text-[11.5px] font-bold uppercase tracking-wide ${group.tone} max-[767px]:!text-[#353434]`}>{group.label}</h3>
                  <span className="text-[11px] font-semibold text-slate-300">{group.items.length}</span>
                </div>
                <div className="space-y-2">
                  {group.items.map((t) => {
                    const names = taskAssigneeNames(t);
                    const isDone = t.status === "done";
                    return (
                      <div key={t.id} data-testid={`task-row-${t.id}`} className={`rounded-xl border-2 md:bg-white p-3.5 flex items-start gap-3 transition-colors max-[767px]:bg-[#FFFFFF] max-[767px]:!text-[#353434] ${isDone ? "border-emerald-300" : "border-rose-300"}`}>
                        <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="mt-0.5 text-slate-400 hover:text-blue-600">
                          {isDone ? <CheckSquare className="h-5 w-5 text-emerald-600" /> : <Square className="h-5 w-5" />}
                        </button>
                        <div className="min-w-0 flex-1 cursor-pointer mobile-task-card-content" data-testid={`task-open-${t.id}`} onClick={() => setOpenTask(t)}>
                          <div className={`text-[13.5px] font-semibold ${isDone ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</div>
                          {t.notes && <div className="text-[12px] text-slate-500 mt-0.5 line-clamp-1">{t.notes}</div>}
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11.5px] text-slate-500">
                            {names.length > 0 && (
                              <span className="inline-flex items-center gap-1">
                                {names.length > 1 ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
                                {names.length <= 2 ? names.join(", ") : `${names[0]} +${names.length - 1}`}
                              </span>
                            )}
                            {t.dueAt && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(t.dueAt)}</span>}
                            {t.linkedCard?.type === "job" && t.linkedCard?.id ? (
                              <Link
                                to={`/jobs?id=${t.linkedCard.id}`}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`task-linked-job-${t.id}`}
                                className="inline-flex items-center gap-1 md:px-1.5 md:py-0.5 md:rounded md:bg-blue-50 text-blue-600 font-semibold hover:underline"
                              >
                                <Briefcase className="h-3 w-3" /> {t.linkedCard.title}
                              </Link>
                            ) : (
                              t.linkedCard?.title && <span className="inline-flex items-center gap-1 md:px-1.5 md:py-0.5 md:rounded md:bg-slate-100 text-slate-500">{t.linkedCard.title}</span>
                            )}
                            {Array.isArray(t.noteLog) && t.noteLog.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-blue-600"><StickyNote className="h-3 w-3" /> {t.noteLog.length}</span>
                            )}
                            {t.source === "automation" && <span className="inline-flex items-center gap-1 text-violet-600"><Bot className="h-3 w-3" /> automation</span>}
                          </div>
                        </div>
                        {canManage && (
                          <button onClick={() => remove(t)} data-testid={`task-delete-${t.id}`} className="h-7 w-7 rounded hover:bg-rose-50 flex items-center justify-center text-slate-300 hover:text-rose-600">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {addOpen && (
        <NewTaskModal
          users={users}
          customers={customers}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); load(); notifyChanged(); toast.success("Task created"); }}
        />
      )}

      {openTask && (
        <TaskDetailModal
          task={openTask}
          users={users}
          customer={customers.find((c) => c.id === openTask.customerId) || null}
          canManage={canManage}
          canNote={canManage || (user && (openTask.assigneeIds || []).includes(user.id))}
          onClose={() => setOpenTask(null)}
          onOpenCustomer={(cid) => setOpenCustomerId(cid)}
          onUpdated={(updated) => {
            const statusChanged = updated.status !== openTask.status;
            setOpenTask(updated);
            setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            if (statusChanged && filter !== "all") setTimeout(load, 250);
          }}
        />
      )}

      {openCustomerId && (
        <CustomerProfileOverlay customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} />
      )}

      {/* Mobile-only floating "+" — same trigger as the desktop Topbar "New task" button */}
      {canManage && (
        <button
          onClick={() => setAddOpen(true)}
          data-testid="mobile-new-task-fab"
          aria-label="New task"
          className="md:hidden fixed right-4 bottom-24 z-40 h-14 w-14 rounded-full flex items-center justify-center shadow-lg active:opacity-90"
          style={{ backgroundColor: "#2563EB", color: "#ffffff", boxShadow: "0 10px 24px rgba(11,30,63,0.35)" }}
        >
          <Plus className="h-7 w-7" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
        </button>
      )}
    </>
  );
}

export function NewTaskModal({ users, customers, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [dueAt, setDueAt] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20";

  const toggleAssignee = (id) =>
    setAssigneeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Disambiguate duplicate display names (e.g. two "Admin" seed accounts)
  // by appending the email only for the names that actually collide.
  const nameCounts = useMemo(() => {
    const counts = {};
    users.forEach((u) => { counts[u.name] = (counts[u.name] || 0) + 1; });
    return counts;
  }, [users]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return setError("Task title is required");
    setBusy(true);
    setError("");
    try {
      await tasksApi.create({ title: title.trim(), notes, assigneeIds, dueAt: dueAt || null, customerId: customerId || null });
      onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create task");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="new-task-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">New task</div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3" data-testid="new-task-form">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-title-input" placeholder="Task title" className={inputCls} />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="task-notes-input" rows={2} placeholder="Notes (optional)" className={inputCls + " h-auto py-2 resize-y"} />
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Assign to (one or more)</label>
            <div className="max-h-36 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="task-assignee-picker">
              {users.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No staff available</div>}
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={assigneeIds.includes(u.id)} onChange={() => toggleAssignee(u.id)} data-testid={`task-assignee-opt-${u.id}`} className="h-3.5 w-3.5" />
                  <span className="text-slate-700">{nameCounts[u.name] > 1 && u.email ? `${u.name} (${u.email})` : u.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Due date</label>
            <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="task-due-input" className={inputCls} />
          </div>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="task-customer-input" className={inputCls}>
            <option value="">Link a customer (optional)</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {error && (
            <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="task-save-btn" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">{busy ? "Saving…" : "Create task"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
